import type { MachineId, TokenSpec } from '../../shared/contract.js';
import { ctxFor } from '../modifier.js';
import { hasTag, type TokenRuntime } from '../token.js';
import { v3, type Vec3 } from '../vec.js';
import { Cabinet, type FieldSolid, type TagTable } from './base.js';

/**
 * DER GREIFER (Preistheke) — **the shop**.
 *
 * The Preistheke is not a menu. It is a claw cabinet stuffed with wrapped prizes, and you
 * pay per grab in TICKETS (economy.md: coins pay debts, tickets buy futures — this is the
 * ticket sink). Whether you get the thing is a question of where the pile happens to be
 * sitting, how you drive, and one honest grip roll.
 *
 * **DECISION — a wrapped prize IS a token.** Prizes are ordinary token bodies in the same
 * world as everything else, with a `contents` entry on the side saying what is inside the
 * paper. This is not a shortcut, it is the whole reason the shop is the crown jewel: it
 * makes machines.md's promises fall out of the grammar rather than out of shop code.
 * [Magnet] in the claw really does drag adjacent prizes along, because the magnet tag
 * default pulls on neighbouring bodies and a prize is a neighbouring body. [Geist] really
 * does phase the prize glass, because ghosting is a collision-mask answer the pipeline
 * gives. [Schwer] prizes really are harder to lift. Not one line of that is written here.
 *
 * **Der Riesenplüschbär** is a prize with a radius larger than the chute. He is a physics
 * problem you bought on purpose, and Der Croupier will say so.
 *
 * machines.md gives this cabinet no structural limit; what it has instead is VERDACHT.
 * Shoplifting by grammar is legal, spectacular and expensive.
 */

export interface GreiferSetzen {
  /** where to send the claw, in cabinet-local units */
  x: number;
  z: number;
  /** index into Der Beutel — the token you clip to the claw (its tags do the work) */
  spec: TokenSpec;
}

export interface PrizeStock {
  /** item id from the catalogue, or a token-roll pseudo-item */
  item: string;
  name: string;
  /** Der Riesenplüschbär and friends: too big for the chute */
  big?: boolean;
  /** ticket worth, used for confiscation valuation */
  worth: number;
}

const CAB = {
  halfWidth: 5.4,
  halfDepth: 4.2,
  glassY: 9.0,
  floorY: 0.4,
  railY: 8.2,
  /** the chute a prize has to fit through to become yours */
  chute: { x: 4.2, z: -3.0, halfWidth: 1.05 },
} as const;

const GRAB_COST_TICKETS = 6;
const CLAW_SPEED = 5.4;
const BASE_GRIP = 0.62;

type ClawPhase = 'idle' | 'travel' | 'descend' | 'close' | 'lift' | 'carry' | 'release';

export class Greifer extends Cabinet<GreiferSetzen> {
  readonly id: MachineId = 'greifer';
  readonly limitLabel = 'Der Schacht — nur etwas wirklich Großes bringt ihn zum Bersten.';
  readonly breakMode = 'chute' as const;

  /**
   * The declared odds mapping. Everything physical ([Magnet] dragging neighbours,
   * [Geist] phasing the glass, [Schwer] weighing the claw down) is deliberately NOT in
   * here — it is already true. Only the two questions physics cannot answer are declared.
   */
  readonly tagTable: TagTable = {
    Gluecks: { effect: 'firmGrip', note: 'Der Greifer fasst diese Marke sicherer.' },
    Fluch: { effect: 'invertGrip', note: 'Dreht Griff- und Fehlgriffchance um.' },
  };

  private solids: FieldSolid[] = [];
  /** tokenId → what is inside the paper */
  private contents = new Map<number, PrizeStock>();
  private clawBody = -1;
  private clawPos: Vec3 = v3(0, CAB.railY, 0);
  private target: Vec3 = v3(0, CAB.railY, 0);
  private phase: ClawPhase = 'idle';
  private phaseT = 0;
  /** the prizes currently in the claw — two of them after Nachlegen ("double or drop") */
  private held: number[] = [];
  private carrying = 0;
  private capacity = 1;
  private grabs = 0;
  private jammed = false;
  private lastGrab = 'noch kein Griff';
  /** prizes that made it out; the run drains this */
  private claimed: PrizeStock[] = [];
  private clipped: number | null = null;

  build(): void {
    const phys = this.host.phys;
    const W = CAB.halfWidth;
    const D = CAB.halfDepth;

    // ---- the vitrine. Glass on top so shoplifting needs [Geist], not luck.
    phys.addStatic({ kind: 'box', hx: W, hy: 0.2, hz: D, pos: this.w(v3(0, CAB.floorY - 0.2, 0)) }, 'ground', 0.75, 0.05);
    for (const s of [-1, 1]) {
      phys.addStatic({ kind: 'box', hx: 0.25, hy: CAB.glassY / 2, hz: D, pos: this.w(v3(s * (W + 0.25), CAB.glassY / 2, 0)) }, 'shell', 0.3, 0.2);
      phys.addStatic({ kind: 'box', hx: W, hy: CAB.glassY / 2, hz: 0.25, pos: this.w(v3(0, CAB.glassY / 2, s * (D + 0.25))) }, 'shell', 0.3, 0.2);
    }
    phys.addStatic({ kind: 'box', hx: W, hy: 0.2, hz: D, pos: this.w(v3(0, CAB.glassY, 0)) }, 'shell', 0.2, 0.2);

    // the chute: a gap in the floor with walls. A prize wider than the mouth will not go.
    const c = CAB.chute;
    for (const s of [-1, 1]) {
      const pos = this.w(v3(c.x + s * (c.halfWidth + 0.3), CAB.floorY + 0.6, c.z));
      phys.addStatic({ kind: 'box', hx: 0.3, hy: 0.6, hz: 1.4, pos }, 'field', 0.4, 0.1);
      this.solids.push({ kind: 'box', pos, h: v3(0.3, 0.6, 1.4) });
    }

    this.clawBody = phys.addKinematicBox(0.8, 0.9, 0.8, this.w(this.clawPos));
  }

  fieldSolids(): readonly FieldSolid[] {
    return this.solids;
  }

  /** The run stocks the cabinet each floor; stock is drawn from the run's item pool. */
  restock(stock: readonly PrizeStock[]): void {
    for (const [id] of this.contents) {
      const t = this.host.api.token(id);
      if (t) this.host.removeToken(id, 'lost');
    }
    this.contents.clear();
    this.held = [];
    this.jammed = false;

    stock.forEach((s, i) => {
      const big = s.big === true;
      const spec: TokenSpec = {
        id: -1,
        // wrapped prizes are obsidian-dark parcels; the material is flavour, and the
        // material stage never sees them because a prize is never scored
        material: 'obsidian',
        faceValue: s.worth,
        radius: big ? 1.9 : 0.72,
        tags: [],
      };
      // spread the pile across the floor deterministically, then let physics settle it
      const a = (i / Math.max(1, stock.length)) * Math.PI * 2;
      const r = 1.2 + this.host.rng.float('greifer:stock') * 2.6;
      const pos = this.w(v3(Math.cos(a) * r, CAB.floorY + 1.2 + i * 0.35, Math.sin(a) * r * 0.7));
      const tok = this.host.spawnToken(spec, this.id, pos, v3(0, -0.4, 0));
      this.contents.set(tok.id, s);
    });
  }

  /** What is still in the cabinet — the shop window, for the UI. */
  stock(): { token: number; name: string; big: boolean; worth: number }[] {
    const out: { token: number; name: string; big: boolean; worth: number }[] = [];
    for (const [token, s] of this.contents) {
      if (!this.host.api.token(token)) continue;
      out.push({ token, name: s.name, big: s.big === true, worth: s.worth });
    }
    out.sort((a, b) => a.token - b.token);
    return out;
  }

  /** Prizes that reached the chute since the last drain. */
  drainClaimed(): PrizeStock[] {
    const out = this.claimed;
    this.claimed = [];
    return out;
  }

  /** Verb 1: Setzen — clip a token to the claw and send it somewhere. */
  setzen(p: GreiferSetzen): void {
    if (this.broken || this.jammed) return;
    if (this.phase !== 'idle') return;
    if (!this.host.economy.spendTickets(GRAB_COST_TICKETS)) {
      this.lastGrab = 'zu wenig Tickets';
      return;
    }

    // The token you clip to the claw is a real token: its tags are the whole build
    // interaction. It rides along and is returned to you when the claw comes home.
    const clip = this.host.spawnToken(p.spec, this.id, this.w(v3(this.clawPos.x, this.clawPos.y - 1.1, this.clawPos.z)), v3(0, 0, 0));
    this.clipped = clip.id;
    this.host.emit({ t: 'drop', token: clip.id, machine: this.id });
    this.host.pipe.fire('onDrop', { ...ctxFor(this.id, this.host.tick, clip), api: this.host.api }, { token: clip.id });

    this.target = v3(
      clamp(p.x, -CAB.halfWidth + 1, CAB.halfWidth - 1),
      CAB.railY,
      clamp(p.z, -CAB.halfDepth + 1, CAB.halfDepth - 1),
    );
    this.phase = 'travel';
    this.phaseT = 0;
    this.grabs++;
  }

  /** Verb 2 reaction: shove the cabinet and the whole pile resettles. Honest and useful. */
  protected override onShove(strength: number): void {
    // a shove during the carry is how you lose what you are holding
    if (this.phase === 'carry' || this.phase === 'lift') {
      for (const id of [...this.held]) {
        if (!this.host.roll('greifer', 'holdThroughShove', 0.55 - 0.2 * strength, this.host.api.token(id))) {
          this.dropHeld(id);
        }
      }
    }
    if (this.jammed && strength > 0.8) {
      // shaking the bear loose is legitimate, and it is the intended answer
      this.jammed = false;
      this.lastGrab = 'Schacht freigerüttelt';
    }
  }

  /** Verb 4b: Nachlegen — "double or drop". Carry two at once; drop either, lose both. */
  protected escalate(level: number): void {
    this.capacity = Math.min(3, 1 + (level - 1));
  }

  /**
   * Verb 3: GLÜCK — re-randomise a wrapped prize's contents *before* unwrapping. The one
   * place in the game where luck buys information rather than bending a physical event.
   */
  override glueckSpend(stream: string, event: string): boolean {
    if (!super.glueckSpend(stream, event)) return false;
    if (event !== 'wrap') return true;
    const targets = this.held.length > 0 ? this.held : this.stock().slice(0, 1).map((s) => s.token);
    for (const id of targets) {
      const cur = this.contents.get(id);
      if (!cur) continue;
      const pool = [...this.contents.values()].filter((s) => s.item !== cur.item);
      if (pool.length === 0) continue;
      const swap = this.host.rng.pick('greifer:wrap', pool);
      this.contents.set(id, { ...swap });
    }
    return true;
  }

  tickMachine(dt: number): void {
    this.tickShared(dt);
    if (this.broken) return;

    this.phaseT += dt;
    switch (this.phase) {
      case 'travel': this.drive(dt, this.target, () => { this.phase = 'descend'; this.phaseT = 0; }); break;
      case 'descend': this.drive(dt, v3(this.clawPos.x, CAB.floorY + 1.1, this.clawPos.z), () => { this.phase = 'close'; this.phaseT = 0; }); break;
      case 'close': this.close(); break;
      case 'lift': this.drive(dt, v3(this.clawPos.x, CAB.railY, this.clawPos.z), () => { this.phase = 'carry'; this.phaseT = 0; }); break;
      case 'carry': this.drive(dt, v3(CAB.chute.x, CAB.railY, CAB.chute.z), () => { this.phase = 'release'; this.phaseT = 0; }); break;
      case 'release': this.release(); break;
      default: break;
    }

    // held prizes ride with the claw. They stay dynamic bodies, so their tags keep working
    // — this is what makes a [Magnet] parcel drag its neighbours out of the pile.
    for (const id of [...this.held]) {
      const t = this.host.api.token(id);
      if (!t || t.body < 0) { this.held = this.held.filter((x) => x !== id); continue; }
      this.host.phys.setTranslation(t.body, v3(this.clawPos.x, this.clawPos.y - 1.5, this.clawPos.z));
      this.host.phys.setLinvel(t.body, v3(0, 0, 0));
    }
    if (this.clipped !== null) {
      const t = this.host.api.token(this.clipped);
      if (t && t.body >= 0 && this.phase !== 'idle') {
        this.host.phys.setTranslation(t.body, v3(this.clawPos.x, this.clawPos.y - 0.9, this.clawPos.z));
        this.host.phys.setLinvel(t.body, v3(0, 0, 0));
      }
    }

    // shoplifting by grammar: a prize that left through the glass is still yours, and the
    // House very much notices. The escape question is asked of the pipeline, as always.
    for (const [id, stock] of [...this.contents]) {
      const t = this.host.api.token(id);
      if (!t) { this.contents.delete(id); continue; }
      const p = this.host.api.position(id);
      if (!p) continue;
      const local = { x: p.x - this.origin.x, y: p.y - this.origin.y, z: p.z - this.origin.z };
      const outside = Math.abs(local.x) > CAB.halfWidth + 0.6 || Math.abs(local.z) > CAB.halfDepth + 0.6;
      if (outside && local.y > CAB.floorY) {
        this.claimed.push(stock);
        this.contents.delete(id);
        this.held = this.held.filter((x) => x !== id);
        this.host.emit({ t: 'grab', prize: stock.name, kept: true });
        this.host.emit({ t: 'item', id: stock.item, name: stock.name, gained: true });
        this.host.economy.addVerdacht(18, 'Ladendiebstahl per Grammatik', (e) => this.host.emit(e));
        this.lastGrab = `${stock.name} — durch das Glas`;
        this.host.removeToken(id, 'transfer');
      }
    }

    this.checkLimit();
  }

  private drive(dt: number, to: Vec3, done: () => void): void {
    const dx = to.x - this.clawPos.x, dy = to.y - this.clawPos.y, dz = to.z - this.clawPos.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    // The House does not emote (ART-BIBLE §7.2): the claw travels at constant velocity.
    const step = CLAW_SPEED * dt;
    if (d <= step || d < 1e-4) {
      this.clawPos = v3(to.x, to.y, to.z);
      this.host.phys.moveKinematic(this.clawBody, this.w(this.clawPos));
      done();
      return;
    }
    this.clawPos = v3(
      this.clawPos.x + (dx / d) * step,
      this.clawPos.y + (dy / d) * step,
      this.clawPos.z + (dz / d) * step,
    );
    this.host.phys.moveKinematic(this.clawBody, this.w(this.clawPos));
  }

  /** The grip roll. One declared, GLÜCK-bendable, [Fluch]-invertible question. */
  private close(): void {
    const near = this.prizesNear(this.clawPos, 1.7);
    if (near.length === 0) {
      this.lastGrab = 'ins Leere gegriffen';
      this.registerDud(this.clipped !== null ? this.host.api.token(this.clipped) : null);
      this.phase = 'lift';
      this.phaseT = 0;
      return;
    }

    const take = near.slice(0, this.capacity);
    for (const id of take) {
      const prize = this.host.api.token(id);
      if (!prize) continue;
      // grip odds: heavier parcels are harder, and the two declared tags apply. The
      // House's weak-grip ZINKEN rewrites this same `odds` query — same door, no cheating.
      let p = BASE_GRIP - Math.min(0.35, (prize.radius - 0.72) * 0.30);
      if (this.tagTable.Gluecks?.effect === 'firmGrip' && this.clipHasTag('Gluecks')) p += 0.18;
      const roll = this.host.roll('greifer', this.tagTable.Fluch && this.clipHasTag('Fluch') ? 'gripInverted' : 'grip', p, prize);
      const got = this.tagTable.Fluch?.effect === 'invertGrip' && this.clipHasTag('Fluch') ? !roll : roll;
      if (got) {
        this.held.push(id);
      } else {
        const s = this.contents.get(id);
        this.host.emit({ t: 'grab', prize: s ? s.name : null, kept: false });
        this.lastGrab = s ? `${s.name} — abgerutscht` : 'abgerutscht';
      }
    }
    if (this.held.length === 0) this.registerDud(this.clipped !== null ? this.host.api.token(this.clipped) : null);
    this.phase = 'lift';
    this.phaseT = 0;
  }

  /** "Double or drop": if you are carrying more than one and drop either, you lose both. */
  private dropHeld(id: number): void {
    const s = this.contents.get(id);
    this.host.emit({ t: 'grab', prize: s ? s.name : null, kept: false });
    this.lastGrab = s ? `${s.name} — verloren` : 'verloren';
    if (this.held.length > 1) {
      for (const other of this.held) {
        const t = this.host.api.token(other);
        if (t && t.body >= 0) this.host.phys.impulse(t.body, v3((this.host.rng.float('greifer:drop') - 0.5) * 3, 1, (this.host.rng.float('greifer:drop') - 0.5) * 3));
      }
      this.held = [];
    } else {
      this.held = this.held.filter((x) => x !== id);
    }
  }

  private release(): void {
    for (const id of [...this.held]) {
      const prize = this.host.api.token(id);
      const stock = this.contents.get(id);
      if (!prize || !stock) { this.held = this.held.filter((x) => x !== id); continue; }

      if (prize.radius > CAB.chute.halfWidth) {
        // Der Riesenplüschbär does not fit down the chute. This is not a failure state,
        // it is the joke and the puzzle: he is yours, he is just not out yet. Split him,
        // take the top glass off, or shake him through.
        this.jammed = true;
        this.lastGrab = `${stock.name} passt nicht durch den Schacht`;
        this.host.emit({ t: 'grab', prize: stock.name, kept: false });
        if (prize.body >= 0) this.host.phys.setTranslation(prize.body, this.w(v3(CAB.chute.x, CAB.floorY + 1.0, CAB.chute.z)));
        this.held = this.held.filter((x) => x !== id);
        continue;
      }

      this.claimed.push(stock);
      this.contents.delete(id);
      this.held = this.held.filter((x) => x !== id);
      this.carrying++;
      this.lastGrab = stock.name;
      this.host.emit({ t: 'grab', prize: stock.name, kept: true });
      this.host.emit({ t: 'item', id: stock.item, name: stock.name, gained: true });
      // a clean purchase pays tickets out as the machine's own performance metric
      this.registerScore(prize, 'preis', prize.faceValue, this.w(v3(CAB.chute.x, CAB.floorY + 1, CAB.chute.z)), false);
      this.host.removeToken(id, 'scored');
    }
    this.returnClip();
    this.phase = 'idle';
    this.phaseT = 0;
    this.target = v3(0, CAB.railY, 0);
  }

  private returnClip(): void {
    if (this.clipped === null) return;
    const t = this.host.api.token(this.clipped);
    if (t) this.host.removeToken(this.clipped, 'transfer');
    this.clipped = null;
  }

  private clipHasTag(tag: Parameters<typeof hasTag>[1]): boolean {
    const t = this.clipped !== null ? this.host.api.token(this.clipped) : null;
    return t ? hasTag(t, tag) : false;
  }

  private prizesNear(at: Vec3, radius: number): number[] {
    const hits: { id: number; d: number }[] = [];
    for (const [id] of this.contents) {
      if (this.held.includes(id)) continue;
      const p = this.host.api.position(id);
      if (!p) continue;
      const dx = p.x - (this.origin.x + at.x);
      const dz = p.z - (this.origin.z + at.z);
      const d = Math.hypot(dx, dz);
      if (d <= radius) hits.push({ id, d });
    }
    hits.sort((a, b) => a.d - b.d || a.id - b.id);
    return hits.map((h) => h.id);
  }

  /**
   * machines.md gives the Preistheke no structural limit. What it has is a chute that a
   * plush bear can wedge shut, and the load is how wedged it is.
   */
  structuralLoad(): number {
    return this.jammed ? 0.85 : 0.05 + Math.min(0.4, this.contents.size * 0.02);
  }

  burstValue(): number {
    let v = 0;
    for (const s of this.contents.values()) v += s.worth;
    return v + 60;
  }

  protected override collectRemaining(): { muenzen: number; tickets: number } {
    this.returnClip();
    this.phase = 'idle';
    return { muenzen: 0, tickets: 0 };
  }

  extra(): unknown {
    return {
      claw: { x: this.clawPos.x, y: this.clawPos.y, z: this.clawPos.z, phase: this.phase, open: this.held.length === 0 },
      target: [this.target.x, this.target.y, this.target.z],
      held: [...this.held],
      clipped: this.clipped,
      capacity: this.capacity,
      jammed: this.jammed,
      grabs: this.grabs,
      carrying: this.carrying,
      lastGrab: this.lastGrab,
      costTickets: GRAB_COST_TICKETS,
      stock: this.stock(),
      chute: { x: CAB.chute.x, z: CAB.chute.z, halfWidth: CAB.chute.halfWidth },
      bounds: { halfWidth: CAB.halfWidth, halfDepth: CAB.halfDepth, floorY: CAB.floorY, railY: CAB.railY },
    };
  }
}

const clamp = (x: number, lo: number, hi: number): number => (x < lo ? lo : x > hi ? hi : x);
